import Navigation from '@/components/navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { User, Target, Bell, Award, Users, FileText, Shield, ChevronRight } from 'lucide-react';

interface AdminCapabilities {
  isHRAdmin: boolean;
  isContentAdmin: boolean;
  isSuperAdmin: boolean;
  isAnyAdmin: boolean;
}

const ProfileNew = () => {
  const [name, setName] = useState('User');
  const [dailyGoal, setDailyGoal] = useState('2.0');
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Fetch admin capabilities
  const { data: adminCapabilities } = useQuery<AdminCapabilities>({
    queryKey: ['admin-capabilities'],
    queryFn: async () => {
      const res = await fetch('/api/admin/content/capabilities', { credentials: 'include' });
      if (!res.ok) {
        // Not an admin, return default
        return { isHRAdmin: false, isContentAdmin: false, isSuperAdmin: false, isAnyAdmin: false };
      }
      return res.json();
    },
    enabled: !!user,
  });

  const handleSave = () => {
    toast({
      title: 'Profile updated!',
      description: 'Your changes have been saved successfully.',
    });
  };

  const achievements = [
    { name: 'First Drop', icon: '💧', unlocked: true },
    { name: 'Week Warrior', icon: '⚡', unlocked: true },
    { name: 'Hydration Hero', icon: '🏆', unlocked: false },
    { name: 'Perfect Month', icon: '🌟', unlocked: false },
  ];

  return (
    <div className="min-h-screen pb-24 bg-background">
      <div className="max-w-md mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Profile</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </div>

        {/* Profile Card */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="w-20 h-20 bg-primary">
              <AvatarFallback className="text-2xl font-bold text-primary-foreground">
                {name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-bold text-foreground">SWYG x {name.toUpperCase()}</h2>
              <p className="text-sm text-muted-foreground">Day: 1</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-foreground">
                Display Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="goal" className="text-foreground">
                Daily Goal (Liters)
              </Label>
              <Input
                id="goal"
                type="number"
                step="0.1"
                value={dailyGoal}
                onChange={(e) => setDailyGoal(e.target.value)}
                className="mt-1"
              />
            </div>

            <Button onClick={handleSave} className="w-full">
              Save Changes
            </Button>
          </div>
        </Card>

        {/* Stats Card */}
        <Card className="p-6 mb-6 bg-pastel-lavender border-none">
          <h3 className="font-semibold text-foreground mb-4">Your Stats</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">47</div>
              <div className="text-xs text-foreground/70">Total Days</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">94L</div>
              <div className="text-xs text-foreground/70">Total Water</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">89%</div>
              <div className="text-xs text-foreground/70">Success Rate</div>
            </div>
          </div>
        </Card>

        {/* Achievements */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">Achievements</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className={`p-4 rounded-xl text-center ${
                  achievement.unlocked ? 'bg-pastel-green' : 'bg-muted/50 opacity-50'
                }`}
              >
                <div className="text-3xl mb-2">{achievement.icon}</div>
                <div className="text-xs font-medium text-foreground">{achievement.name}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Admin Panels - Only show if user has admin capabilities */}
        {adminCapabilities?.isAnyAdmin && (
          <Card className="p-6 mb-6 bg-gradient-to-br from-purple-50 to-lilac-100 border-purple-200">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-foreground">Admin Panels</h3>
            </div>
            <div className="space-y-3">
              {(adminCapabilities.isHRAdmin || adminCapabilities.isSuperAdmin) && (
                <button
                  onClick={() => setLocation('/hr-panel')}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-white/60 hover:bg-white/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-purple-600" />
                    <div className="text-left">
                      <span className="text-sm font-medium text-foreground block">
                        HR Admin Panel
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Manage users, reports & awards
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
              {(adminCapabilities.isContentAdmin || adminCapabilities.isSuperAdmin) && (
                <button
                  onClick={() => setLocation('/content-admin')}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-white/60 hover:bg-white/80 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <div className="text-left">
                      <span className="text-sm font-medium text-foreground block">
                        Content Admin Panel
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Manage content & messaging
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
            </div>
          </Card>
        )}

        {/* Settings */}
        <Card className="p-6">
          <h3 className="font-semibold text-foreground mb-4">Settings</h3>
          <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-foreground">Notifications</span>
              </div>
              <span className="text-xs text-muted-foreground">Enabled</span>
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-foreground">Reminder Frequency</span>
              </div>
              <span className="text-xs text-muted-foreground">Every 2 hours</span>
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-foreground">Account Type</span>
              </div>
              <span className="text-xs text-muted-foreground">Free</span>
            </button>
          </div>
        </Card>
      </div>

      <Navigation />
    </div>
  );
};

export default ProfileNew;
